/**
 * Phase 17.7-05 — update-plan API surface (pure helpers).
 *
 * Bridges C++'s FGDB_UpdatePlan (see Plan 17.7-04) to React. Zod-validated
 * parsing + summary helper used by NotificationBell to render the unified
 * plan-summary row. Pure — no React, no fetch, no bridge.
 *
 * S-4 schema lock: downloadUrl is a full HTTPS URL (presigned R2/S3).
 * The C++ side always populates it; Zod rejects relative keys to fail
 * loudly if the contract ever drifts.
 */

import { z } from 'zod'

export const updatePlanEntrySchema = z.object({
  productId: z.string(),
  pluginName: z.string(),
  currentVersion: z.string(),
  targetVersion: z.string(),
  // S-4 — full HTTPS, not optional, not a relative key
  downloadUrl: z.string().url(),
  sha256: z.string(),
  minCoreVersion: z.string().optional(),
  // GitLab #312 — mirrors the source-tier fields C++ adds to
  // FGDB_UpdatePlanEntry. `buildType` echoes the cloud-resolved tier
  // ("compiled" | "source"); `isSourceTier` is the convenience flag the bell's
  // plan summary branches on. Default-safe: a legacy cloud/plugin that omits
  // these still parses, with isSourceTier defaulting to false (never
  // undefined) so consumers branch on `!isSourceTier` without a nullish guard.
  // Type/parse only here — plan-apply behaviour is unchanged (the C++ guard is
  // the safety net for a source build, never an in-place swap).
  buildType: z.string().optional(),
  isSourceTier: z.boolean().optional().default(false),
})
export type UpdatePlanEntry = z.infer<typeof updatePlanEntrySchema>

export const updatePlanSchema = z.object({
  coreUpgrade: updatePlanEntrySchema.nullable(),
  siblingUpgrades: z.array(updatePlanEntrySchema),
  batchId: z.string(),
})
export type UpdatePlan = z.infer<typeof updatePlanSchema>

/** Tolerant parser. Returns null when the input doesn't match the schema. */
export function parseUpdatePlanResponse(raw: unknown): UpdatePlan | null {
  // C++ may return either a parsed object or a JSON string — handle both.
  let input: unknown = raw
  if (typeof raw === 'string') {
    try {
      input = JSON.parse(raw)
    } catch {
      return null
    }
  }
  const result = updatePlanSchema.safeParse(input)
  return result.success ? result.data : null
}

/** Friendly UI name lookup (productId → name). Falls back to productId. */
const FRIENDLY_NAMES: Record<string, string> = {
  gamedevbuddy: 'Core',
  'character-creator': 'Character Creator',
  forge: 'Forge',
  echo: 'Echo',
  prism: 'Prism',
  hoard: 'Hoard',
  demeter: 'Demeter',
  'atlas-painter': 'Atlas Painter',
  agora: 'Agora',
  oracle: 'Oracle',
}
function friendly(productId: string): string {
  return FRIENDLY_NAMES[productId] ?? productId
}

/** Produces 'Core 0.7.52 + Character Creator 0.7.51 available (2 plugins)' style. */
export function summarizePlan(plan: UpdatePlan): string {
  const entries: UpdatePlanEntry[] = []
  if (plan.coreUpgrade) entries.push(plan.coreUpgrade)
  entries.push(...plan.siblingUpgrades)
  if (entries.length === 0) return ''

  const parts = entries.map((e) => `${friendly(e.productId)} ${e.targetVersion}`)
  if (entries.length === 1) return `${parts[0]} available`
  return `${parts.join(' + ')} available (${entries.length} plugins)`
}

export function isEmptyPlan(plan: UpdatePlan): boolean {
  return !plan.coreUpgrade && plan.siblingUpgrades.length === 0
}
